import React, { createContext, useContext, useEffect, useState } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenValidationInterval, setTokenValidationInterval] = useState(null);

  // Initialize authentication state
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Simply check if we have stored auth data
        if (authAPI.isAuthenticated()) {
          const currentUser = authAPI.getCurrentUser();
          // Validate token is still valid by making a test API call
          try {
            const validationResponse = await authAPI.validateToken();
            // Use the fresh user data from server, not the stale localStorage data
            const freshUser = validationResponse.user || currentUser;
            
            // Preserve impersonation data if it exists in current user
            if (currentUser?.isImpersonation) {
              freshUser.isImpersonation = currentUser.isImpersonation;
              freshUser.impersonatedBy = currentUser.impersonatedBy;
              freshUser.impersonatedByEmail = currentUser.impersonatedByEmail;
            }
            
            setUser(freshUser);
            // Update localStorage with fresh user data
            localStorage.setItem('user', JSON.stringify(freshUser));
          } catch (validationError) {
            // Token is invalid, clear auth data
            console.log('Token validation failed, clearing auth state');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            localStorage.removeItem('onboardingProgress');
            localStorage.removeItem('searchExecutionState');
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear potentially corrupted auth data
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('onboardingProgress');
        localStorage.removeItem('searchExecutionState');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Set up periodic token validation
  useEffect(() => {
    if (user && !tokenValidationInterval) {
      // Validate token every 5 minutes
      const interval = setInterval(async () => {
        try {
          const validationResponse = await authAPI.validateToken();
          // Update user with fresh data from server
          if (validationResponse.user) {
            const freshUser = validationResponse.user;
            
            // Preserve impersonation data if it exists
            if (user?.isImpersonation) {
              freshUser.isImpersonation = user.isImpersonation;
              freshUser.impersonatedBy = user.impersonatedBy;
              freshUser.impersonatedByEmail = user.impersonatedByEmail;
            }
            
            setUser(freshUser);
            localStorage.setItem('user', JSON.stringify(freshUser));
          }
        } catch (error) {
          console.log('Token validation failed, logging out');
          // Clear interval before logout
          if (interval) {
            clearInterval(interval);
          }
          // Clear auth data immediately but preserve theme
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('onboardingProgress');
          localStorage.removeItem('searchExecutionState');
          setUser(null);
          setError(null);
          setTokenValidationInterval(null);
          // Use replace to prevent back button issues
          window.location.replace('/');
        }
      }, 5 * 60 * 1000); // 5 minutes

      setTokenValidationInterval(interval);
    }

    // Cleanup interval on unmount or when user logs out
    return () => {
      if (tokenValidationInterval) {
        clearInterval(tokenValidationInterval);
        setTokenValidationInterval(null);
      }
    };
  }, [user, tokenValidationInterval]);

  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.login(credentials);
      setUser(response.user);
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.register(userData);
      setUser(response.user);
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Clear interval if running
    if (tokenValidationInterval) {
      clearInterval(tokenValidationInterval);
      setTokenValidationInterval(null);
    }
    // Clear auth data but preserve theme and other non-auth settings
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('onboardingProgress');
    localStorage.removeItem('searchExecutionState');
    // Keep 'theme' in localStorage
    setUser(null);
    setError(null);
    // Use replace to prevent back button issues
    window.location.replace('/');
  };

  const updateUser = (userData) => {
    setUser(prevUser => {
      const updatedUser = { ...prevUser, ...userData };
      // Also update localStorage to persist the changes
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    updateUser,
    clearError,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;